<template>
  <div ref="container" class="column-container">
    <svg ref="svg" class="overflow-visible text-gray-500 fill-current" />
  </div>
</template>

<script>
// https://observablehq.com/@d3/donut-chart
import * as d3 from "d3";
export default {
  name: "ColumnChart",
  props: {
    data: {
      type: Array,
      default: () => [
        { name: "Val 1", value: 50, color: "#eeeeee" },
        { name: "Val 2", value: 100, color: "#cccccc" },
      ],
    },
    max: {
      type: Number,
      default: null,
    },
    color: {
      type: String,
      default: "#ccc",
    },
    duration: {
      type: Number,
      default: 300,
    },
    average: {
      type: Number,
      default: null,
    },
    averageText: {
      type: String,
      default: "media total",
    },
    margin: {
      type: Object,
      default: () => ({ top: 0, right: 0, bottom: 30, left: 20 }),
    },
  },
  data() {
    return {
      width: 500,
      height: 200,
      hoverIndex: null,
    };
  },
  computed: {
    computedMax() {
      if (this.max) return this.max;
      return d3.max(this.data, (d) => d.value);
    },
    x() {
      return d3
        .scaleBand()
        .domain(this.data.map((e) => e.name))
        .range([this.margin.left, this.width - this.margin.right])
        .padding(0.1);
    },
    y() {
      return d3
        .scaleLinear()
        .domain([0, this.computedMax])
        .nice()
        .range([this.height - this.margin.bottom, this.margin.top]);
    },
  },
  watch: {
    data() {
      this.draw();
    },
  },
  mounted() {
    this.setSize();
    this.createChart();
    if (ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.resize();
      });
      this.resizeObserver.observe(this.$refs.container);
    } else {
      // for IE
      window.addEventListener("resize", this.resize);
    }
  },
  beforeDestroy() {
    if (ResizeObserver) {
      this.resizeObserver.unobserve(this.$refs.container);
    } else {
      // for IE
      window.removeEventListener("resize", this.resize);
    }
  },
  methods: {
    setSize() {
      const container = this.$refs.container;
      if (container) {
        this.width = container.clientWidth;
        this.height = container.clientHeight;
      }
    },
    resize() {
      const currentWidth = this.width;
      const currentHeight = this.height;
      this.setSize();
      if (currentWidth !== this.width || currentHeight !== this.height) {
        this.updateChart();
      }
    },
    draw(delay = this.duration, duration = this.duration) {
      this.drawAxis(delay, duration);

      this.rects
        .attr("fill", this.color)
        .selectAll("rect")
        .data(this.data, (d) => d.name)
        .join(
          (enter) =>
            enter
              .append("rect")
              // .attr("fill", (d) => d.data.color)
              .attr("fill", this.color)
              .attr("x", (d) => this.x(d.name))
              .attr("y", () => this.y(0))
              .attr("height", 0)
              .attr("width", this.x.bandwidth())
              .on("mouseenter", (event, data) =>
                this.$emit("hover", { event, data })
              )
              .on("mouseleave", () => this.$emit("hover", null))
              .call((enter) =>
                enter
                  .transition()
                  .duration(duration)
                  .delay(delay)
                  .attr("y", (d) => this.y(d.value))
                  .attr("height", (d) => this.y(0) - this.y(d.value))
              ),
          (update) =>
            update
              //.transition()
              .attr("x", (d) => this.x(d.name))
              .attr("y", (d) => this.y(d.value))
              .attr("height", (d) => this.y(0) - this.y(d.value))
              .attr("width", this.x.bandwidth()),
          (exit) =>
            exit
              .transition()
              .duration(duration)
              .attr("y", () => this.y(0))
              .attr("height", 0)
              .remove()
        );

      // average line
      if (this.average !== null) {
        this.avg
          .attr("display", this.average !== null ? null : "none")
          .call((g) =>
            g
              .select("line")
              .attr("x1", this.margin.left)
              .attr("x2", this.width)
          )
          .transition()
          .duration(duration)
          .delay(delay)
          .attr("transform", `translate(0,${this.y(this.average)})`);
      }
    },
    drawAxis(delay, duration) {
      // AXIS
      this.xAxis
        .attr(
          "transform",
          "translate(-0.5," + (this.height - this.margin.bottom) + ")"
        )
        .transition()
        .duration(duration)
        .delay(delay)
        .call(
          d3.axisBottom(this.x) //.ticks(this.numTicks)
          // .tickFormat((d) => this.data[0][d]?.label || d)
        );
      //.call((g) => g.select(".domain").remove());

      this.yAxis
        .attr("transform", `translate(${this.width},0)`)
        .transition()
        .duration(duration)
        .delay(delay)
        .call(
          d3
            .axisLeft(this.y)
            .tickSize(this.width - this.margin.left - this.margin.right)
        );
    },
    updateChart() {
      this.svg.attr("viewBox", [0, 0, this.width, this.height]);
      this.draw(0, 0);
    },
    createChart() {
      if (!this.svg) {
        this.svg = d3
          .select(this.$refs.svg)
          .attr("viewBox", [0, 0, this.width, this.height]);

        this.yAxis = this.svg
          .append("g")
          .attr("class", "y-axis")
          .attr("transform", `translate(${this.width},0)`);
        // .attr("transform", `translate(${this.margin.left},0)`);
        // .attr("fill", this.textColor)

        this.rects = this.svg.append("g");

        this.xAxis = this.svg
          .append("g")
          .attr("class", "x-axis")
          .attr(
            "transform",
            "translate(-0.5," + (this.height - this.margin.bottom) + ")"
          );

        this.avg = this.svg
          .append("g")
          .attr("class", "average")
          .attr("display", this.average !== null ? null : "none");

        this.avg.append("line").attr("stroke", "black");

        this.avg
          .append("text")
          .attr("class", "text-xs")
          .attr("fill", "black")
          .attr("x", this.margin.left)
          .attr("dy", "-4px")
          .text(this.averageText);

        //first paint with no delay
        this.draw(0);
      }
    },
  },
};
</script>

<style>
.y-axis .tick line {
  stroke-opacity: 0.2;
  /* stroke-dasharray: 2, 2; */
}
.y-axis .domain {
  display: none;
}
</style>
